import React, { useState, useEffect } from 'react';
import {
  ArrowLeft, Plus, User, Phone, FileText, Zap, CreditCard, Smartphone,
  CheckCircle, Clock, AlertCircle, XCircle, Bell, Send, ChevronRight,
  TrendingUp, Users, DollarSign, Target, Eye, Edit2, X, Check,
  MapPin, Calendar, MessageSquare, Star, Trash2, RefreshCw, Lock, Copy, Save
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch } from '../services/api';
import './EsteiraVendas.css';

// ── Controle de acesso da Esteira por cargo ───────────────────────────────────
const ESTEIRA_ACCESS = {
  admin:       { prevenda: true,  posvenda: true  },
  vendedor:    { prevenda: true,  posvenda: false  },
  'pós vendas':{ prevenda: false, posvenda: true  },
  'pos vendas':{ prevenda: false, posvenda: true  },
  posvenda:    { prevenda: false, posvenda: true  },
  atendente:   { prevenda: false, posvenda: false  },
  financeiro:  { prevenda: false, posvenda: false  },
  marketing:   { prevenda: false, posvenda: false  },
};

function calcEsteiraPerms(roles) {
  const arr = Array.isArray(roles) ? roles : [roles];
  const normalized = arr.map((r) => (r || '').toLowerCase().trim());
  return {
    prevenda: normalized.some((r) => ESTEIRA_ACCESS[r]?.prevenda),
    posvenda: normalized.some((r) => ESTEIRA_ACCESS[r]?.posvenda),
  };
}

const statusVendaConfig = {
  rascunho:          { label: 'Rascunho',          color: 'gray',   Icon: FileText },
  novo:              { label: 'Novo Lead',         color: 'blue',    Icon: Star        },
  em_contato:        { label: 'Em Contato',        color: 'amber',   Icon: Phone       },
  enviar_contrato:   { label: 'Contrato Enviado',  color: 'indigo',  Icon: FileText    },
  negociando:        { label: 'Negociando',        color: 'purple',  Icon: TrendingUp  },
  fechado:           { label: 'Fechado ✓',         color: 'green',   Icon: CheckCircle },
  perdido:           { label: 'Perdido',           color: 'red',     Icon: XCircle     },
};

const statusPosConfig = {
  pendente:     { label: 'Pendente',    color: 'amber', Icon: Clock        },
  em_andamento: { label: 'Em Andamento',color: 'blue',  Icon: RefreshCw   },
  concluido:    { label: 'Concluído',   color: 'green', Icon: CheckCircle },
};

const modalidadeIcon = { CPFL: <Zap size={13} />, Asaas: <CreditCard size={13} />, Pix: <Smartphone size={13} /> };
const modalidadeClass = { CPFL: 'type-cpfl', Asaas: 'type-asaas', Pix: 'type-pix' };

function Badge({ config, value }) {
  const cfg = config[value];
  if (!cfg) return null;
  return (
    <span className={`ev-badge badge-${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

function Toast({ msg, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }, [onClose]);
  return <div className="ev-toast"><Bell size={15} /> {msg}</div>;
}

function Modal({ title, onClose, children }) {
  return (
    <div className="ev-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ev-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ev-modal-header">
          <h3>{title}</h3>
          <button className="ev-modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="ev-modal-body">{children}</div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// PRÉ-VENDA: formulário de novo lead (com campo VALOR e SELETOR DE MODELO)
// ────────────────────────────────────────────────────────────────────────────
const emptyPreVenda = {
  titularNome: '', titularCpf: '', titularRg: '',
  titularContato: '', titularUc: '', titularNascimento: '',
  nome: '', cpf: '', rg: '', contato: '', nascimento: '',
  modalidade: 'CPFL', endereco: '', vendedor: '', observacao: '',
  valor: '',  // ⭐ NOVO
  modeloId: '', // ⭐ ID do template de contrato selecionado
};

function PreVendaForm({ onBack, onSalvar, onSalvarRascunho, modelos, loadingModelos }) {
  const [form, setForm]         = useState(emptyPreVenda);
  const [saving, setSaving]     = useState(false);
  const [ok, setOk]             = useState(false);
  const [titularDoPlano, setTitularDoPlano] = useState(false);
  const [contratoEnviando, setContratoEnviando] = useState(false);
  const [linkAssinatura, setLinkAssinatura] = useState(null);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleTitularDoPlano = (checked) => {
    setTitularDoPlano(checked);
    if (checked) {
      setForm((p) => ({
        ...p,
        nome: p.titularNome,
        cpf: p.titularCpf,
        rg: p.titularRg,
        contato: p.titularContato,
        nascimento: p.titularNascimento,
      }));
    } else {
      setForm((p) => ({
        ...p,
        nome: '', cpf: '', rg: '', contato: '', nascimento: '',
      }));
    }
  };

  const setTitular = (k, v) => {
    setForm((p) => {
      const updated = { ...p, [k]: v };
      if (titularDoPlano) {
        const map = {
          titularNome:      'nome',
          titularCpf:       'cpf',
          titularRg:        'rg',
          titularContato:   'contato',
          titularNascimento:'nascimento',
        };
        if (map[k]) updated[map[k]] = v;
      }
      return updated;
    });
  };

  const isCPFL = form.modalidade === 'CPFL';

  // ⭐ Função que gera o contrato e envia para assinatura
  const gerarContratoEEnviar = async (leadId = null) => {
    if (!form.modeloId) {
      alert('Selecione um modelo de contrato antes de salvar.');
      return null;
    }
    const tokenClicksign = localStorage.getItem('clicksign_token');
    if (!tokenClicksign) {
      alert('Token da Clicksign não encontrado. Por favor, configure-o na aba "Modelos de Documentos" em Administração.');
      return null;
    }

    setContratoEnviando(true);
    try {
      // Dados para preencher o PDF (placeholders)
      const dadosPlaceholders = {
        nome: form.nome,
        cpf: form.cpf,
        rg: form.rg,
        contato: form.contato,
        nascimento: form.nascimento,
        endereco: form.endereco,
        modalidade: form.modalidade,
        valor: parseFloat(form.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 }),
        vendedor: form.vendedor,
        dataVenda: new Date().toLocaleDateString('pt-BR'),
        ...(isCPFL && {
          titularNome: form.titularNome,
          titularCpf: form.titularCpf,
          titularRg: form.titularRg,
          titularContato: form.titularContato,
          titularUc: form.titularUc,
          titularNascimento: form.titularNascimento,
        }),
      };

      const response = await apiFetch('/clicksign/preencher-e-enviar', {
        method: 'POST',
        body: JSON.stringify({
          templateId: form.modeloId,
          dados: dadosPlaceholders,
          apiToken: tokenClicksign,
          cliente: {
            nome: form.nome,
            email: form.contato, // se o contato for email, senão use um campo específico
          },
        }),
      });

      if (response.success) {
        const signUrl = response.signers?.[0]?.sign_url;
        setLinkAssinatura(signUrl);
        // Se tiver um leadId, podemos atualizar o lead com envelope_id e link
        if (leadId) {
          await apiFetch(`/api/leads/${leadId}`, {
            method: 'PUT',
            body: JSON.stringify({
              envelope_id: response.envelope_id,
              contratoLink: signUrl,
              status: 'enviar_contrato',
            }),
          });
        }
        return response;
      } else {
        throw new Error(response.error || 'Erro ao gerar contrato');
      }
    } catch (err) {
      console.error(err);
      alert(`Erro ao gerar contrato: ${err.message}`);
      return null;
    } finally {
      setContratoEnviando(false);
    }
  };

  const handleSalvarRascunho = () => {
    if (!form.nome || !form.contato) return;
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      setOk(true);
      onSalvarRascunho(form);
      setTimeout(() => {
        setOk(false);
        setForm(emptyPreVenda);
        setTitularDoPlano(false);
        setLinkAssinatura(null);
      }, 2000);
    }, 1200);
  };

  const handleSalvar = async () => {
    if (!form.nome || !form.contato) return;
    if (!form.modeloId) {
      alert('Selecione um modelo de contrato antes de salvar.');
      return;
    }

    setSaving(true);
    try {
      // 1. Criar o lead no banco (rascunho temporário)
      const novoLead = {
        ...form,
        dataVenda: new Date().toISOString().slice(0, 10),
        horaVenda: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
        status: 'rascunho',
        notificado: false,
        valor: parseFloat(form.valor) || 0,
      };
      const created = await apiFetch('/api/leads', { method: 'POST', body: JSON.stringify(novoLead) });

      // 2. Gerar contrato e enviar para assinatura
      const contratoResult = await gerarContratoEEnviar(created.id);
      if (contratoResult && contratoResult.success) {
        // Atualizar status do lead para 'enviar_contrato' e guardar envelope_id
        await apiFetch(`/api/leads/${created.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            status: 'enviar_contrato',
            envelope_id: contratoResult.envelope_id,
            contratoLink: contratoResult.signers?.[0]?.sign_url,
          }),
        });
        setOk(true);
        setTimeout(() => {
          setOk(false);
          onBack();
        }, 2400);
      } else {
        throw new Error('Falha ao gerar contrato');
      }
    } catch (err) {
      console.error(err);
      alert('Erro ao salvar lead e enviar contrato: ' + err.message);
    } finally {
      setSaving(false);
      setContratoEnviando(false);
    }
  };

  return (
    <div className="ev-form-page">
      <button className="ev-back-btn" onClick={onBack}><ArrowLeft size={15} /> Voltar</button>

      <div className="ev-form-topbar">
        <div>
          <h2 className="ev-form-title">Nova Pré-Venda</h2>
          <p className="ev-form-sub">Preencha os dados coletados na visita ao cliente</p>
        </div>
        <div className="ev-form-badge-row">
          {['CPFL','Asaas','Pix'].map((m) => (
            <button
              key={m}
              className={`ev-modal-select ${form.modalidade === m ? 'active-' + m.toLowerCase() : ''}`}
              onClick={() => set('modalidade', m)}
            >
              {m === 'CPFL' && <Zap size={14} />}
              {m === 'Asaas' && <CreditCard size={14} />}
              {m === 'Pix' && <Smartphone size={14} />}
              {m}
            </button>
          ))}
        </div>
      </div>

      {(saving || ok || contratoEnviando) && (
        <div className="ev-save-overlay">
          <div className="ev-save-box">
            {contratoEnviando && <><div className="ev-spinner" /><p>Gerando contrato e enviando para assinatura...</p></>}
            {saving && !contratoEnviando && <><div className="ev-spinner" /><p>Salvando lead...</p></>}
            {ok && <><div className="ev-check-anim">✓</div><p>Lead cadastrado e contrato enviado para assinatura! 📄</p></>}
          </div>
        </div>
      )}

      <div className="ev-form-card">
        {isCPFL && (
          <div className="ev-form-block">
            <div className="ev-form-block-title"><Zap size={15} /> Titular da Conta de Luz</div>
            <div className="ev-form-grid four">
              <div className="ev-field full-2">
                <label>NOME DO TITULAR</label>
                <input type="text" value={form.titularNome} onChange={(e) => setTitular('titularNome', e.target.value)} placeholder="Nome completo do titular" />
              </div>
              <div className="ev-field">
                <label>CPF DO TITULAR</label>
                <input type="text" value={form.titularCpf} onChange={(e) => setTitular('titularCpf', e.target.value)} placeholder="000.000.000-00" />
              </div>
              <div className="ev-field">
                <label>RG DO TITULAR</label>
                <input type="text" value={form.titularRg} onChange={(e) => setTitular('titularRg', e.target.value)} placeholder="RG" />
              </div>
              <div className="ev-field">
                <label>CONTATO TITULAR</label>
                <input type="text" value={form.titularContato} onChange={(e) => setTitular('titularContato', e.target.value)} placeholder="(00) 00000-0000" />
              </div>
              <div className="ev-field">
                <label>UC (Unidade Consumidora)</label>
                <input type="text" value={form.titularUc} onChange={(e) => setTitular('titularUc', e.target.value)} placeholder="UC da conta de luz" />
              </div>
              <div className="ev-field">
                <label>DATA DE NASCIMENTO</label>
                <input type="date" value={form.titularNascimento} onChange={(e) => setTitular('titularNascimento', e.target.value)} />
              </div>
            </div>
          </div>
        )}

        <div className="ev-form-block">
          <div className="ev-form-block-title">
            <User size={15} /> Dados do Cliente (Comprador)
            {isCPFL && (
              <label className="ev-titular-plano-label">
                <div className={`ev-titular-plano-check ${titularDoPlano ? 'checked' : ''}`} onClick={() => handleTitularDoPlano(!titularDoPlano)}>
                  {titularDoPlano && <Check size={11} />}
                </div>
                <span onClick={() => handleTitularDoPlano(!titularDoPlano)}>Titular do Plano</span>
              </label>
            )}
          </div>
          <div className="ev-form-grid four">
            <div className="ev-field full-2">
              <label>NOME COMPLETO *</label>
              <input
                type="text"
                value={form.nome}
                onChange={(e) => set('nome', e.target.value)}
                placeholder="Nome completo"
                readOnly={titularDoPlano}
                className={titularDoPlano ? 'ev-field-readonly' : ''}
              />
            </div>
            <div className="ev-field">
              <label>CPF</label>
              <input
                type="text"
                value={form.cpf}
                onChange={(e) => set('cpf', e.target.value)}
                placeholder="000.000.000-00"
                readOnly={titularDoPlano}
                className={titularDoPlano ? 'ev-field-readonly' : ''}
              />
            </div>
            <div className="ev-field">
              <label>RG</label>
              <input
                type="text"
                value={form.rg}
                onChange={(e) => set('rg', e.target.value)}
                placeholder="RG"
                readOnly={titularDoPlano}
                className={titularDoPlano ? 'ev-field-readonly' : ''}
              />
            </div>
            <div className="ev-field">
              <label>CONTATO *</label>
              <input
                type="text"
                value={form.contato}
                onChange={(e) => set('contato', e.target.value)}
                placeholder="(00) 00000-0000"
                readOnly={titularDoPlano}
                className={titularDoPlano ? 'ev-field-readonly' : ''}
              />
            </div>
            <div className="ev-field">
              <label>DATA DE NASCIMENTO</label>
              <input
                type="date"
                value={form.nascimento}
                onChange={(e) => set('nascimento', e.target.value)}
                readOnly={titularDoPlano}
                className={titularDoPlano ? 'ev-field-readonly' : ''}
              />
            </div>
          </div>
        </div>

        <div className="ev-form-block">
          <div className="ev-form-block-title"><Target size={15} /> Dados da Venda</div>
          <div className="ev-form-grid four">
            <div className="ev-field full-2">
              <label>ENDEREÇO DO CLIENTE</label>
              <input type="text" value={form.endereco} onChange={(e) => set('endereco', e.target.value)} placeholder="Rua, número, bairro, cidade/UF" />
            </div>
            <div className="ev-field">
              <label>NOME DO VENDEDOR</label>
              <input type="text" value={form.vendedor} onChange={(e) => set('vendedor', e.target.value)} placeholder="Seu nome completo" />
            </div>
            <div className="ev-field">
              <label>VALOR DA VENDA (R$)</label>
              <input
                type="number"
                step="0.01"
                value={form.valor}
                onChange={(e) => set('valor', e.target.value)}
                placeholder="0,00"
              />
            </div>
            <div className="ev-field full">
              <label>OBSERVAÇÕES</label>
              <input type="text" value={form.observacao} onChange={(e) => set('observacao', e.target.value)} placeholder="Observações sobre a visita, cliente, condições..." />
            </div>
          </div>
        </div>

        {/* ⭐ SELETOR DE MODELO DE CONTRATO */}
        <div className="ev-form-block">
          <div className="ev-form-block-title"><FileText size={15} /> Modelo de Contrato</div>
          <div className="ev-form-grid">
            <div className="ev-field full">
              <select
                value={form.modeloId}
                onChange={(e) => set('modeloId', e.target.value)}
                style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                disabled={loadingModelos}
              >
                <option value="">Selecione um modelo</option>
                {modelos.map(m => (
                  <option key={m.id} value={m.id}>{m.nome} ({m.categoria})</option>
                ))}
              </select>
              {loadingModelos && <small>Carregando modelos...</small>}
            </div>
          </div>
        </div>

        <div className="ev-preview-msg">
          <div className="ev-preview-title"><Bell size={14} /> Notificação que o Pós-Venda receberá:</div>
          <div className="ev-preview-bubble">
            🔔 <strong>NOVO LEAD CADASTRADO!</strong><br />
            👤 Cliente: <strong>{form.nome || '[nome]'}</strong><br />
            📞 Contato: <strong>{form.contato || '[contato]'}</strong><br />
            ⚡ Modalidade: <strong>{form.modalidade}</strong><br />
            💰 Valor: <strong>R$ {form.valor ? parseFloat(form.valor).toFixed(2) : '0,00'}</strong><br />
            👨‍💼 Vendedor: <strong>{form.vendedor || '[vendedor]'}</strong><br />
            📍 {form.endereco || '[endereço]'}<br />
            <span style={{ fontSize: 11, opacity: 0.7 }}>⏰ {new Date().toLocaleString('pt-BR')}</span>
          </div>
        </div>

        <div className="ev-form-actions">
          <button className="ev-btn-cancel" onClick={onBack}>Cancelar</button>
          <button className="ev-btn-secondary" onClick={handleSalvarRascunho}>
            <Save size={15} /> Salvar
          </button>
          <button className="ev-btn-save" onClick={handleSalvar} disabled={saving || contratoEnviando}>
            <Send size={15} /> Salvar e Enviar Contrato
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// MODAL UPLOAD DE CONTRATO (venda externa)
// ────────────────────────────────────────────────────────────────────────────
function UploadContratoModal({ lead, onClose, onConfirm }) {
  const [file,  setFile]  = useState(null);
  const [ready, setReady] = useState(false);

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (f) { setFile(f); setReady(true); }
  };

  return (
    <div className="ev-overlay" onClick={onClose}>
      <div className="ev-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ev-modal-header">
          <h3>📎 Upload de Contrato — Venda Externa</h3>
          <button className="ev-modal-close" onClick={onClose}><X size={18}/></button>
        </div>
        <div className="ev-modal-body">
          <div className="ev-upload-info">
            <div className="ev-upload-avatar">{lead.nome.charAt(0)}</div>
            <div>
              <div className="ev-upload-nome">{lead.nome}</div>
              <div className="ev-upload-meta">Vendedor: {lead.vendedor} · {lead.modalidade} · Valor: R$ {(lead.valor || 0).toFixed(2)}</div>
            </div>
          </div>
          <div className="ev-upload-instrucoes">
            <FileText size={14}/>
            <span>Faça upload do contrato assinado pelo cliente para fechar a venda e enviar ao Pós-Venda automaticamente.</span>
          </div>
          <div className="ev-upload-area" onClick={() => document.getElementById('contrato-input').click()}>
            {file ? (
              <div className="ev-upload-file-ok">
                <CheckCircle size={28} color="#059669"/>
                <div>
                  <div className="ev-upload-fname">{file.name}</div>
                  <div className="ev-upload-fsize">{(file.size / 1024).toFixed(1)} KB · Clique para trocar</div>
                </div>
              </div>
            ) : (
              <div className="ev-upload-placeholder">
                <FileText size={32} color="#94a3b8"/>
                <span>Clique para selecionar o contrato</span>
                <small>PDF, DOC, DOCX, JPG, PNG</small>
              </div>
            )}
            <input id="contrato-input" type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" style={{ display:'none' }} onChange={handleFile}/>
          </div>
        </div>
        <div className="ev-modal-footer">
          <button className="ev-btn-cancel" onClick={onClose}>Cancelar</button>
          <button
            className="ev-btn-save"
            style={{ opacity: ready ? 1 : 0.5, cursor: ready ? 'pointer' : 'not-allowed' }}
            onClick={() => ready && onConfirm(lead.id, file)}
          >
            <CheckCircle size={15}/> Confirmar Venda e Enviar ao Pós-Venda
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// CARD DE LEAD (com exibição do valor e link do contrato)
// ────────────────────────────────────────────────────────────────────────────
function LeadCard({ lead, onVerDetalhes, onUpdateStatus, onUpdatePosStatus, onEnviarContrato, onUploadContrato, isPosVenda }) {
  const cfg = statusVendaConfig[lead.status];
  const [showUpload, setShowUpload] = useState(false);

  const handleStatusChange = (e) => {
    const val = e.target.value;
    if (val === 'enviar_contrato') {
      onEnviarContrato(lead.id);
    } else {
      onUpdateStatus(lead.id, val);
    }
  };

  return (
    <div className={`ev-lead-card ${lead.status === 'novo' && !lead.notificado ? 'card-novo' : ''} ${lead.status === 'enviar_contrato' ? 'card-contrato' : ''}`}>
      {showUpload && (
        <UploadContratoModal
          lead={lead}
          onClose={() => setShowUpload(false)}
          onConfirm={(id, file) => { onUploadContrato(id, file); setShowUpload(false); }}
        />
      )}
      <div className="ev-lead-header">
        <div className="ev-lead-avatar">{lead.nome.charAt(0)}</div>
        <div className="ev-lead-info">
          <div className="ev-lead-nome">{lead.nome}</div>
          <div className="ev-lead-meta"><Phone size={12}/> {lead.contato}</div>
          <div className="ev-lead-meta"><MapPin size={12}/> {lead.endereco}</div>
        </div>
        <span className={`ev-badge badge-${cfg?.color}`}>{cfg?.label}</span>
      </div>
      <div className="ev-lead-dados">
        <div className="ev-lead-row">
          <span>Modalidade</span>
          <span className={`type-badge ${modalidadeClass[lead.modalidade]}`}>
            {modalidadeIcon[lead.modalidade]} {lead.modalidade}
          </span>
        </div>
        <div className="ev-lead-row"><span>Vendedor</span><strong>{lead.vendedor}</strong></div>
        <div className="ev-lead-row"><span>Valor (R$)</span><strong>R$ {(lead.valor || 0).toLocaleString('pt-BR')}</strong></div>
        <div className="ev-lead-row"><span>Data</span><strong>{lead.dataVenda} {lead.horaVenda}</strong></div>
        {lead.observacao && (
          lead.observacao === 'Confirmado e feito ligação'
            ? <div className="ev-lead-obs-confirmado">✅ {lead.observacao}</div>
            : <div className="ev-lead-obs">{lead.observacao}</div>
        )}
        {lead.status === 'enviar_contrato' && (
          <div className="ev-contrato-status">
            <div className="ev-contrato-aguardando"><RefreshCw size={13} className="ev-spin"/> Aguardando retorno do contrato assinado...</div>
            {lead.contratoLink && (
              <div className="ev-contrato-link">
                <a href={lead.contratoLink} target="_blank" rel="noopener noreferrer">🔗 Acessar contrato para assinatura</a>
              </div>
            )}
            <div className="ev-contrato-hint">Quando o sistema detectar a assinatura, o lead será enviado automaticamente ao Pós-Venda.</div>
          </div>
        )}
        {lead.contratoArquivo && (
          <div className="ev-lead-row">
            <span>Contrato</span>
            <span className="ev-contrato-file"><CheckCircle size={11} color="#059669"/> {lead.contratoArquivo}</span>
          </div>
        )}
        {isPosVenda && lead.notificado && (
          <div className="ev-lead-row">
            <span>Pós-Venda</span>
            {lead.posStatus
              ? <Badge config={statusPosConfig} value={lead.posStatus}/>
              : <span className="ev-badge badge-amber">Aguardando</span>}
          </div>
        )}
      </div>
      {!lead.notificado && isPosVenda && (
        <div className="ev-lead-novo-alert"><Bell size={13}/> Novo lead aguardando seu contato!</div>
      )}
      <div className="ev-lead-actions">
        <button className="ev-lead-btn view" onClick={() => onVerDetalhes(lead)}>
          <Eye size={14}/> Ver Detalhes
        </button>
        {!isPosVenda && (
          <>
            <select className="ev-lead-select" value={lead.status} onChange={handleStatusChange}>
              <option value="novo">Novo Lead</option>
              <option value="em_contato">Em Contato</option>
              <option value="enviar_contrato">📧 Enviar Contrato</option>
              <option value="negociando">Negociando</option>
              <option value="fechado">Fechado</option>
              <option value="perdido">Perdido</option>
            </select>
            <button className="ev-btn-upload-contrato" title="Upload de contrato físico (venda externa)" onClick={() => setShowUpload(true)}>
              <FileText size={14}/> Upload Contrato
            </button>
          </>
        )}
        {isPosVenda && (
          <select className="ev-lead-select" value={lead.posStatus || ''} onChange={(e) => onUpdatePosStatus(lead.id, e.target.value)}>
            <option value="">Atualizar status</option>
            <option value="pendente">Pendente</option>
            <option value="em_andamento">Em Andamento</option>
            <option value="concluido">Concluído</option>
          </select>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// MODAL DE DETALHES DO LEAD (com edição do valor)
// ────────────────────────────────────────────────────────────────────────────
function LeadDetalhes({ lead, onClose, onUpdateObs, onUpdateLead }) {
  const [editMode, setEditMode]   = useState(false);
  const [obsVenda, setObsVenda]   = useState(lead.observacao);
  const [obsPos,   setObsPos]     = useState(lead.obs_posvenda);
  const [saved,    setSaved]      = useState(false);

  const [editData, setEditData] = useState({
    nome:              lead.nome,
    cpf:               lead.cpf,
    rg:                lead.rg,
    contato:           lead.contato,
    nascimento:        lead.nascimento,
    titularNome:       lead.titularNome,
    titularCpf:        lead.titularCpf,
    titularRg:         lead.titularRg,
    titularContato:    lead.titularContato,
    titularUc:         lead.titularUc,
    titularNascimento: lead.titularNascimento,
    endereco:          lead.endereco,
    vendedor:          lead.vendedor,
    modalidade:        lead.modalidade,
    valor:             lead.valor || 0,
  });

  const isCPFL = editMode ? editData.modalidade === 'CPFL' : lead.modalidade === 'CPFL';

  const setEdit = (k, v) => setEditData((p) => ({ ...p, [k]: v }));

  const handleSave = () => {
    if (editMode) {
      onUpdateLead(lead.id, { ...editData, observacao: obsVenda, obs_posvenda: obsPos });
      setEditMode(false);
    } else {
      onUpdateObs(lead.id, obsVenda, obsPos);
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleCancelEdit = () => {
    setEditData({
      nome:              lead.nome,
      cpf:               lead.cpf,
      rg:                lead.rg,
      contato:           lead.contato,
      nascimento:        lead.nascimento,
      titularNome:       lead.titularNome,
      titularCpf:        lead.titularCpf,
      titularRg:         lead.titularRg,
      titularContato:    lead.titularContato,
      titularUc:         lead.titularUc,
      titularNascimento: lead.titularNascimento,
      endereco:          lead.endereco,
      vendedor:          lead.vendedor,
      modalidade:        lead.modalidade,
      valor:             lead.valor || 0,
    });
    setObsVenda(lead.observacao);
    setObsPos(lead.obs_posvenda);
    setEditMode(false);
  };

  return (
    <Modal title="Detalhes do Lead" onClose={onClose}>
      <div className="ev-det-header">
        <div className="ev-det-avatar">{editMode ? editData.nome.charAt(0) || '?' : lead.nome.charAt(0)}</div>
        <div>
          <div className="ev-det-nome">{editMode ? editData.nome || '—' : lead.nome}</div>
          <div className="ev-det-meta">CPF: {editMode ? editData.cpf || '—' : lead.cpf} · RG: {editMode ? editData.rg || '—' : lead.rg}</div>
          <div className="ev-det-meta">📞 {editMode ? editData.contato || '—' : lead.contato}</div>
          {(editMode ? editData.nascimento : lead.nascimento) && (
            <div className="ev-det-meta">🎂 {editMode ? editData.nascimento : lead.nascimento}</div>
          )}
        </div>
        <Badge config={statusVendaConfig} value={lead.status} />
      </div>
      {saved && <div className="ev-saved-msg"><Check size={13} /> Salvo com sucesso!</div>}

      {!editMode && (
        <>
          {isCPFL && (
            <div className="ev-det-section">
              <div className="ev-det-section-title"><Zap size={13} /> Titular da Conta de Luz</div>
              <div className="ev-det-grid">
                <div className="ev-det-row"><span>Nome</span><strong>{lead.titularNome}</strong></div>
                <div className="ev-det-row"><span>CPF</span><strong>{lead.titularCpf}</strong></div>
                <div className="ev-det-row"><span>RG</span><strong>{lead.titularRg}</strong></div>
                <div className="ev-det-row"><span>Contato</span><strong>{lead.titularContato}</strong></div>
                <div className="ev-det-row"><span>UC</span><strong>{lead.titularUc}</strong></div>
                <div className="ev-det-row"><span>Nascimento</span><strong>{lead.titularNascimento}</strong></div>
              </div>
            </div>
          )}
          <div className="ev-det-section">
            <div className="ev-det-section-title"><Target size={13} /> Dados da Venda</div>
            <div className="ev-det-grid">
              <div className="ev-det-row"><span>Modalidade</span><span className={`type-badge ${modalidadeClass[lead.modalidade]}`}>{modalidadeIcon[lead.modalidade]} {lead.modalidade}</span></div>
              <div className="ev-det-row"><span>Vendedor</span><strong>{lead.vendedor}</strong></div>
              <div className="ev-det-row"><span>Valor (R$)</span><strong>R$ {(lead.valor || 0).toLocaleString('pt-BR')}</strong></div>
              <div className="ev-det-row"><span>Data/Hora</span><strong>{lead.dataVenda} {lead.horaVenda}</strong></div>
              <div className="ev-det-row"><span>Endereço</span><strong>{lead.endereco}</strong></div>
            </div>
          </div>
          <div className="ev-obs-section">
            <div className="ev-obs-label"><MessageSquare size={13} /> Observação do Vendedor</div>
            <textarea value={obsVenda} onChange={(e) => setObsVenda(e.target.value)} rows={2} placeholder="Observações da visita..." />
          </div>
          <div className="ev-obs-section">
            <div className="ev-obs-label" style={{ color: '#7c3aed' }}><MessageSquare size={13} /> Observação do Pós-Venda</div>
            <textarea value={obsPos} onChange={(e) => setObsPos(e.target.value)} rows={2} placeholder="Observações do pós-venda..." />
          </div>
        </>
      )}

      {editMode && (
        <>
          <div className="ev-det-section">
            <div className="ev-det-section-title"><Target size={13} /> Modalidade</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
              {['CPFL', 'Asaas', 'Pix'].map((m) => (
                <button key={m} className={`ev-modal-select ${editData.modalidade === m ? 'active-' + m.toLowerCase() : ''}`} onClick={() => setEdit('modalidade', m)} style={{ fontSize: 13, padding: '6px 14px' }}>
                  {m === 'CPFL' && <Zap size={13} />}{m === 'Asaas' && <CreditCard size={13} />}{m === 'Pix' && <Smartphone size={13} />}{m}
                </button>
              ))}
            </div>
          </div>
          {isCPFL && (
            <div className="ev-det-section">
              <div className="ev-det-section-title"><Zap size={13} /> Titular da Conta de Luz</div>
              <div className="ev-form-grid four" style={{ gap: 10 }}>
                <div className="ev-field full-2"><label>NOME DO TITULAR</label><input type="text" value={editData.titularNome} onChange={(e) => setEdit('titularNome', e.target.value)} placeholder="Nome completo do titular" /></div>
                <div className="ev-field"><label>CPF DO TITULAR</label><input type="text" value={editData.titularCpf} onChange={(e) => setEdit('titularCpf', e.target.value)} placeholder="000.000.000-00" /></div>
                <div className="ev-field"><label>RG DO TITULAR</label><input type="text" value={editData.titularRg} onChange={(e) => setEdit('titularRg', e.target.value)} placeholder="RG" /></div>
                <div className="ev-field"><label>CONTATO TITULAR</label><input type="text" value={editData.titularContato} onChange={(e) => setEdit('titularContato', e.target.value)} placeholder="(00) 00000-0000" /></div>
                <div className="ev-field"><label>UC (Unidade Consumidora)</label><input type="text" value={editData.titularUc} onChange={(e) => setEdit('titularUc', e.target.value)} placeholder="UC da conta de luz" /></div>
                <div className="ev-field"><label>DATA DE NASCIMENTO</label><input type="text" value={editData.titularNascimento} onChange={(e) => setEdit('titularNascimento', e.target.value)} placeholder="DD/MM/AAAA" /></div>
              </div>
            </div>
          )}
          <div className="ev-det-section">
            <div className="ev-det-section-title"><User size={13} /> Dados do Cliente (Comprador)</div>
            <div className="ev-form-grid four" style={{ gap: 10 }}>
              <div className="ev-field full-2"><label>NOME COMPLETO</label><input type="text" value={editData.nome} onChange={(e) => setEdit('nome', e.target.value)} placeholder="Nome completo" /></div>
              <div className="ev-field"><label>CPF</label><input type="text" value={editData.cpf} onChange={(e) => setEdit('cpf', e.target.value)} placeholder="000.000.000-00" /></div>
              <div className="ev-field"><label>RG</label><input type="text" value={editData.rg} onChange={(e) => setEdit('rg', e.target.value)} placeholder="RG" /></div>
              <div className="ev-field"><label>CONTATO</label><input type="text" value={editData.contato} onChange={(e) => setEdit('contato', e.target.value)} placeholder="(00) 00000-0000" /></div>
              <div className="ev-field"><label>DATA DE NASCIMENTO</label><input type="text" value={editData.nascimento} onChange={(e) => setEdit('nascimento', e.target.value)} placeholder="DD/MM/AAAA" /></div>
            </div>
          </div>
          <div className="ev-det-section">
            <div className="ev-det-section-title"><Target size={13} /> Dados da Venda</div>
            <div className="ev-form-grid four" style={{ gap: 10 }}>
              <div className="ev-field full-2"><label>ENDEREÇO DO CLIENTE</label><input type="text" value={editData.endereco} onChange={(e) => setEdit('endereco', e.target.value)} placeholder="Rua, número, bairro, cidade/UF" /></div>
              <div className="ev-field"><label>NOME DO VENDEDOR</label><input type="text" value={editData.vendedor} onChange={(e) => setEdit('vendedor', e.target.value)} placeholder="Nome do vendedor" /></div>
              <div className="ev-field"><label>VALOR (R$)</label><input type="number" step="0.01" value={editData.valor} onChange={(e) => setEdit('valor', e.target.value)} placeholder="0,00" /></div>
            </div>
          </div>
          <div className="ev-obs-section"><div className="ev-obs-label"><MessageSquare size={13} /> Observação do Vendedor</div><textarea value={obsVenda} onChange={(e) => setObsVenda(e.target.value)} rows={2} placeholder="Observações da visita..." /></div>
          <div className="ev-obs-section"><div className="ev-obs-label" style={{ color: '#7c3aed' }}><MessageSquare size={13} /> Observação do Pós-Venda</div><textarea value={obsPos} onChange={(e) => setObsPos(e.target.value)} rows={2} placeholder="Observações do pós-venda..." /></div>
        </>
      )}

      <div className="ev-modal-footer">
        {!editMode ? (
          <>
            <button className="ev-btn-cancel" onClick={onClose}>Fechar</button>
            <button className="ev-lead-btn view" style={{ padding: '9px 16px', fontSize: 14 }} onClick={() => setEditMode(true)}><Edit2 size={14} /> Editar</button>
            <button className="ev-btn-save" onClick={handleSave}><Check size={14} /> Salvar</button>
          </>
        ) : (
          <>
            <button className="ev-btn-cancel" onClick={handleCancelEdit}><X size={14} style={{ marginRight: 4 }} /> Cancelar</button>
            <button className="ev-btn-save" onClick={handleSave}><Check size={14} /> Salvar Alterações</button>
          </>
        )}
      </div>
    </Modal>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// COMPONENTE PRINCIPAL
// ────────────────────────────────────────────────────────────────────────────
export default function EsteiraVendas() {
  const [view, setView] = useState('menu');
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [detalhes, setDetalhes] = useState(null);
  const [filterStatus, setFilterStatus] = useState('todos');
  const [search, setSearch] = useState('');
  const [modelos, setModelos] = useState([]);
  const [loadingModelos, setLoadingModelos] = useState(false);

  const { user } = useAuth();
  const userRoles = user?.roles || (user?.role ? [user.role] : ['Vendedor']);
  const perms = calcEsteiraPerms(userRoles);

  const showToast = (msg) => setToast(msg);

  // Carregar modelos de contrato disponíveis (rota acessível a qualquer usuário autenticado)
const loadModelos = async () => {
  setLoadingModelos(true);
  try {
    // ✅ Adicione "/api" antes de "/document-templates"
    const data = await apiFetch('/api/document-templates');
    setModelos(data);
  } catch (err) {
    console.error('Erro ao carregar modelos:', err);
    showToast('Erro ao carregar modelos de contrato');
  } finally {
    setLoadingModelos(false);
  }
};

  const loadLeads = async () => {
    if (!view || (view !== 'prevenda' && view !== 'posvenda')) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (view === 'posvenda') params.append('view', 'posvenda');
      if (filterStatus !== 'todos') params.append('filterStatus', filterStatus);
      if (search) params.append('search', search);
      const data = await apiFetch(`/api/leads?${params.toString()}`);
      setLeads(data);
    } catch (err) {
      console.error('Erro ao carregar leads:', err);
      showToast('Erro ao carregar leads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (view === 'prevenda' || view === 'posvenda') {
      loadLeads();
    }
    // Carregar modelos quando o componente montar (para usar no formulário)
    loadModelos();
  }, [view, filterStatus, search]);

  const handleSalvarLeadNotificar = async (form) => {
    // Esta função agora é usada apenas para rascunho, pois o fluxo principal foi alterado
    try {
      const novoLead = {
        ...form,
        dataVenda: new Date().toISOString().slice(0, 10),
        horaVenda: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
        status: 'rascunho',
        notificado: false,
        valor: parseFloat(form.valor) || 0,
      };
      const created = await apiFetch('/api/leads', { method: 'POST', body: JSON.stringify(novoLead) });
      setLeads(prev => [created, ...prev]);
      setView('prevenda');
      showToast('✅ Lead salvo como rascunho.');
    } catch (err) {
      console.error(err);
      showToast('Erro ao salvar lead');
    }
  };

  const handleSalvarLeadRascunho = async (form) => {
    try {
      const novoLead = {
        ...form,
        dataVenda: new Date().toISOString().slice(0, 10),
        horaVenda: new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
        status: 'rascunho',
        notificado: false,
        valor: parseFloat(form.valor) || 0,
      };
      await apiFetch('/api/leads', { method: 'POST', body: JSON.stringify(novoLead) });
      await loadLeads();
      showToast('✅ Lead salvo como rascunho!');
    } catch (err) {
      console.error(err);
      showToast('Erro ao salvar rascunho');
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const payload = { status };
      if (status === 'fechado') payload.observacao = '';
      const updated = await apiFetch(`/api/leads/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
      setLeads(prev => prev.map(l => l.id === id ? updated : l));
      showToast('Status atualizado!');
    } catch (err) {
      console.error(err);
      showToast('Erro ao atualizar status');
    }
  };

  const updatePosStatus = async (id, posStatus) => {
    try {
      const payload = { posStatus, notificado: true };
      if (posStatus === 'concluido') payload.observacao = 'Confirmado e feito ligação';
      const updated = await apiFetch(`/api/leads/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
      setLeads(prev => prev.map(l => l.id === id ? updated : l));
      showToast('Status do pós-venda atualizado!');
    } catch (err) {
      console.error(err);
      showToast('Erro ao atualizar status');
    }
  };

  const updateObs = async (id, observacao, obs_posvenda) => {
    try {
      const updated = await apiFetch(`/api/leads/${id}`, { method: 'PUT', body: JSON.stringify({ observacao, obs_posvenda }) });
      setLeads(prev => prev.map(l => l.id === id ? updated : l));
      showToast('Observação salva!');
    } catch (err) {
      console.error(err);
      showToast('Erro ao salvar observação');
    }
  };

  const updateLead = async (id, data) => {
    try {
      const updated = await apiFetch(`/api/leads/${id}`, { method: 'PUT', body: JSON.stringify(data) });
      setLeads(prev => prev.map(l => l.id === id ? updated : l));
      setDetalhes(updated);
      showToast('✅ Lead atualizado com sucesso!');
    } catch (err) {
      console.error(err);
      showToast('Erro ao atualizar lead');
    }
  };

  const marcarNotificado = async (id) => {
    try {
      await apiFetch(`/api/leads/${id}`, { method: 'PUT', body: JSON.stringify({ notificado: true }) });
      setLeads(prev => prev.map(l => l.id === id ? { ...l, notificado: true } : l));
    } catch (err) {
      console.error(err);
    }
  };

  const handleEnviarContrato = async (id) => {
    try {
      await apiFetch(`/api/leads/${id}`, { method: 'PUT', body: JSON.stringify({ status: 'enviar_contrato', notificado: true }) });
      await loadLeads();
      showToast('📧 Contrato enviado! Aguardando assinatura do cliente...');
      setTimeout(async () => {
        const lead = leads.find(l => l.id === id);
        if (lead && lead.status === 'enviar_contrato') {
          await apiFetch(`/api/leads/${id}`, { method: 'PUT', body: JSON.stringify({ status: 'fechado', posStatus: 'pendente', notificado: true }) });
          await loadLeads();
          showToast('✅ Contrato assinado detectado! Lead encaminhado ao Pós-Venda.');
        }
      }, 8000);
    } catch (err) {
      console.error(err);
      showToast('Erro ao enviar contrato');
    }
  };

  const handleUploadContrato = async (id, file) => {
    try {
      await apiFetch(`/api/leads/${id}`, { method: 'PUT', body: JSON.stringify({ status: 'fechado', posStatus: 'pendente', notificado: true, contratoArquivo: file.name }) });
      await loadLeads();
      showToast(`✅ Contrato "${file.name}" enviado! Lead encaminhado ao Pós-Venda.`);
    } catch (err) {
      console.error(err);
      showToast('Erro ao enviar contrato');
    }
  };

  const getLeadsFiltrados = (isPosVenda) => {
    let lista = isPosVenda ? leads.filter(l => l.notificado || l.status !== 'novo') : leads;
    if (filterStatus !== 'todos') {
      lista = lista.filter(l => isPosVenda ? l.posStatus === filterStatus : l.status === filterStatus);
    }
    if (search) {
      const term = search.toLowerCase();
      lista = lista.filter(l => l.nome.toLowerCase().includes(term) || l.contato.includes(term) || l.vendedor.toLowerCase().includes(term));
    }
    return lista;
  };

  const stats = {
    total: leads.length,
    novos: leads.filter(l => l.status === 'novo').length,
    fechados: leads.filter(l => l.status === 'fechado').length,
    perdidos: leads.filter(l => l.status === 'perdido').length,
    taxa: leads.length > 0 ? Math.round((leads.filter(l => l.status === 'fechado').length / leads.length) * 100) : 0,
  };

  if (view === 'novo') {
    return <PreVendaForm
      onBack={() => setView('prevenda')}
      onSalvar={handleSalvarLeadNotificar}
      onSalvarRascunho={handleSalvarLeadRascunho}
      modelos={modelos}
      loadingModelos={loadingModelos}
    />;
  }

  if (view === 'menu') {
    return (
      <div className="ev-page">
        {toast && <Toast msg={toast} onClose={() => setToast(null)} />}
        <div className="ev-header">
          <div>
            <h1>Esteira de Vendas</h1>
            <p className="ev-header-sub">Gestão completa do ciclo de vendas porta a porta</p>
          </div>
          {user && (
            <div className="ev-user-roles">
              <span>🔐 Seus acessos:</span>
              {userRoles.map((r) => (<span key={r} className="ev-user-role-badge">{r}</span>))}
            </div>
          )}
        </div>
        <div className="ev-stats-row">
          {[
            { label: 'Total de Leads', value: stats.total,    color: 'blue'   },
            { label: 'Novos',          value: stats.novos,    color: 'amber'  },
            { label: 'Fechados',       value: stats.fechados, color: 'green'  },
            { label: 'Perdidos',       value: stats.perdidos, color: 'red'    },
            { label: 'Taxa de Conv.',  value: `${stats.taxa}%`, color: 'purple'},
          ].map((s) => (
            <div key={s.label} className={`ev-stat stat-${s.color}`}>
              <div className="ev-stat-val">{s.value}</div>
              <div className="ev-stat-label">{s.label}</div>
            </div>
          ))}
        </div>
        <div className="ev-teams-title">Escolha a equipe</div>
        <div className="ev-menu-cards">
          <div className="ev-team-block">
            <div className="ev-team-label ev-team-label-pre"><Send size={14} /> EQUIPE PRÉ-VENDA</div>
            <div className={`ev-menu-card card-prevenda ${!perms.prevenda ? 'card-locked' : ''}`} onClick={() => perms.prevenda && setView('prevenda')}>
              <div className="ev-menu-card-icon"><Send size={32} /></div>
              <div className="ev-menu-card-content">
                <div className="ev-menu-card-title">Pré-Venda{!perms.prevenda && <span className="ev-card-lock-badge"><Lock size={11}/> Sem acesso</span>}</div>
                <div className="ev-menu-card-desc">{perms.prevenda ? 'Vendedores de campo cadastram leads das visitas porta a porta. Os dados são enviados automaticamente ao Pós-Venda.' : 'Acesso restrito à Equipe de Pré-Venda (cargo: Vendedor ou Admin).'}</div>
                {perms.prevenda && (<div className="ev-menu-card-stats"><span>📋 {leads.length} leads cadastrados</span><span>·</span><span className="ev-novos-pill">{leads.filter(l => l.status === 'novo').length} novos</span><span>·</span><span>{leads.filter(l => l.status === 'negociando').length} negociando</span></div>)}
              </div>
              {perms.prevenda ? <ChevronRight size={22} className="ev-menu-arrow" /> : <Lock size={20} className="ev-menu-arrow" style={{ color:'#cbd5e1' }} />}
            </div>
          </div>
          <div className="ev-team-block">
            <div className="ev-team-label ev-team-label-pos"><CheckCircle size={14} /> EQUIPE PÓS-VENDA</div>
            <div className={`ev-menu-card card-posvenda ${!perms.posvenda ? 'card-locked' : ''}`} onClick={() => perms.posvenda && setView('posvenda')}>
              <div className="ev-menu-card-icon"><CheckCircle size={32} /></div>
              <div className="ev-menu-card-content">
                <div className="ev-menu-card-title">Pós-Venda{!perms.posvenda && <span className="ev-card-lock-badge"><Lock size={11}/> Sem acesso</span>}</div>
                <div className="ev-menu-card-desc">{perms.posvenda ? 'Equipe interna acompanha, contata e finaliza os leads recebidos. Mantenha o cliente atualizado em cada etapa do processo.' : 'Acesso restrito à Equipe de Pós-Venda (cargo: Pós Vendas ou Admin).'}</div>
                {perms.posvenda && (<div className="ev-menu-card-stats"><span>✅ {leads.filter(l => l.posStatus === 'concluido').length} concluídos</span><span>·</span>{leads.filter(l => !l.notificado).length > 0 ? <span className="ev-novos-pill red">🔔 {leads.filter(l => !l.notificado).length} aguardando atenção!</span> : <span>Tudo em dia ✓</span>}</div>)}
              </div>
              {perms.posvenda ? (<>{leads.filter(l => !l.notificado).length > 0 && <div className="ev-notif-dot">{leads.filter(l => !l.notificado).length}</div>}<ChevronRight size={22} className="ev-menu-arrow" /></>) : <Lock size={20} className="ev-menu-arrow" style={{ color:'#cbd5e1' }} />}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isPosVenda = view === 'posvenda';
  const listaFiltrada = getLeadsFiltrados(isPosVenda);

  return (
    <div className="ev-page">
      {toast && <Toast msg={toast} onClose={() => setToast(null)} />}
      {detalhes && <LeadDetalhes lead={detalhes} onClose={() => setDetalhes(null)} onUpdateObs={updateObs} onUpdateLead={updateLead} />}
      <div className={`ev-team-header ${isPosVenda ? 'pos' : 'pre'}`}>
        <div className="ev-team-header-left">
          <button className="ev-back-btn" onClick={() => { setView('menu'); setFilterStatus('todos'); setSearch(''); }}><ArrowLeft size={15} /> Voltar</button>
          <div className="ev-team-header-badge">{isPosVenda ? <><CheckCircle size={20} /> <span>EQUIPE PÓS-VENDA</span></> : <><Send size={20} /> <span>EQUIPE PRÉ-VENDA</span></>}</div>
          <div><h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#0f172a' }}>{isPosVenda ? 'Pós-Venda' : 'Pré-Venda'}</h2><p className="ev-header-sub" style={{ margin: 0 }}>{isPosVenda ? 'Acompanhe e finalize os leads recebidos dos vendedores' : 'Gerencie os leads cadastrados pelas equipes de campo'}</p></div>
        </div>
        {!isPosVenda && (<button className="ev-btn-novo" onClick={() => setView('novo')}><Plus size={15} /> Novo Lead</button>)}
      </div>
      {isPosVenda && leads.filter(l => !l.notificado).length > 0 && (
        <div className="ev-alert-novos"><Bell size={16} /><strong>{leads.filter(l => !l.notificado).length} novo(s) lead(s)</strong> aguardando seu contato!<button className="ev-alert-btn" onClick={() => leads.filter(l => !l.notificado).forEach(l => marcarNotificado(l.id))}>Marcar todos como vistos</button></div>
      )}
      <div className="ev-list-filters">
        <div className="ev-search-box"><User size={14} /><input type="text" placeholder="Buscar por nome, contato ou vendedor..." value={search} onChange={(e) => setSearch(e.target.value)} /></div>
        <select className="ev-filter-sel" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="todos">Todos os status</option>
          {isPosVenda ? (<><option value="pendente">Pendente</option><option value="em_andamento">Em Andamento</option><option value="concluido">Concluído</option></>) : (<><option value="novo">Novo Lead</option><option value="em_contato">Em Contato</option><option value="enviar_contrato">Contrato Enviado</option><option value="negociando">Negociando</option><option value="fechado">Fechado</option><option value="perdido">Perdido</option></>)}
        </select>
        <span className="ev-count">{listaFiltrada.length} lead(s)</span>
      </div>
      {loading ? (<div style={{ textAlign: 'center', padding: 40 }}>Carregando leads...</div>) : listaFiltrada.length === 0 ? (<div className="ev-empty"><Target size={48} /><p>Nenhum lead encontrado</p>{!isPosVenda && <button className="ev-btn-novo" onClick={() => setView('novo')}><Plus size={14} /> Cadastrar primeiro lead</button>}</div>) : (
        <div className="ev-leads-grid">{listaFiltrada.map((lead) => (<LeadCard key={lead.id} lead={lead} isPosVenda={isPosVenda} onVerDetalhes={(l) => { setDetalhes(l); if (!l.notificado) marcarNotificado(l.id); }} onUpdateStatus={updateStatus} onUpdatePosStatus={updatePosStatus} onEnviarContrato={handleEnviarContrato} onUploadContrato={handleUploadContrato} />))}</div>
      )}
    </div>
  );
}